import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface RegionDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    businessCategory_Id: number;
}

export interface RegionState {
    regions: RegionDto[];
    region: RegionDto;
    gettingRegions: boolean;
    gettingRegion: boolean;
    archivingRegion: boolean;
    savingRegion: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const blankRegionDto: RegionDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
    businessCategory_Id: 0,
};

const initialState: RegionState = {
    regions: [],
    region: blankRegionDto,
    gettingRegions: false,
    gettingRegion: false,
    archivingRegion: false,
    savingRegion: false,
    status: 'idle',
    error: undefined,
};

export const regionSlice = createSlice({
    name: 'regions',
    initialState,
    reducers: {
        clearRegions: (state) => {
            state.regions = [];
            state.region = blankRegionDto;
        },
        updateRegion: (state, param) => {
            const { payload } = param;
            state.region = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getRegions.pending, (state, action) => {
                state.gettingRegions = true;
                state.status = 'loading';
            })
            .addCase(getRegions.fulfilled, (state, action) => {
                state.gettingRegions = false;
                state.regions = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getRegions.rejected, (state, action) => {
                state.gettingRegions = false;
                state.regions = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getRegion.pending, (state, action) => {
                state.gettingRegion = true;
                state.status = 'loading';
            })
            .addCase(getRegion.fulfilled, (state, action) => {
                state.gettingRegion = false;
                state.region = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getRegion.rejected, (state, action) => {
                state.gettingRegion = false;
                state.region = blankRegionDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(saveRegion.pending, (state, action) => {
                state.savingRegion = true;
                state.status = 'loading';
            })
            .addCase(saveRegion.fulfilled, (state, action) => {
                state.savingRegion = false;
                state.status = 'succeeded';
            })
            .addCase(saveRegion.rejected, (state, action) => {
                state.savingRegion = false;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveRegion.pending, (state, action) => {
                state.archivingRegion = true;
                state.status = 'loading';
            })
            .addCase(archiveRegion.fulfilled, (state, action) => {
                state.archivingRegion = false;
                state.regions = state.regions.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveRegion.rejected, (state, action) => {
                state.archivingRegion = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearRegions } = regionSlice.actions;
export const { updateRegion } = regionSlice.actions;

export const getRegions = createAsyncThunk('regions/getRegions', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/regions`);

    return response.data;
});

export const getRegion = createAsyncThunk('regions/getRegion', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/regions/${id}`);

    return response.data;
});

export const archiveRegion = createAsyncThunk('regions/archiveRegion', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/regions/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedRegion'),
        })
    );
    return response.data;
});

export const saveRegion = createAsyncThunk('regions/saveRegion', async (regionToSave: RegionDto, thunkAPI) => {
    if (regionToSave.id === 0) {
        const response = await axios.post(`/api/synergy/regions/create`, regionToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('createRegion'),
            })
        );
        return response.data;
    } else {
        const response = await axios.put(`/api/synergy/regions/put`, regionToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('saveRegion'),
            })
        );
        return response.data;
    }
});
